import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const { TextArea } = Input;

const apiErrorMessage = (err, fallback) => {
  const body = err?.response?.data;
  const msg =
    (body && typeof body === "object" && body.message) ||
    (typeof body === "string" ? body : null) ||
    err?.message ||
    fallback;
  const code = err?.response?.status;
  return typeof code === "number" ? `${msg} (HTTP ${code})` : msg;
};

export default function IngredientCategoryManager() {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [form] = Form.useForm();

  const fetchRows = async () => {
    setLoading(true);
    await request({
      url: apiPath.authAdminIngredientCategoriesList,
      method: "GET",
      onSuccess: (data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
      },
      onError: (err) => {
        ShowToast(
          apiErrorMessage(err, "Failed to load categories"),
          Severty.ERROR,
        );
      },
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      description: record.description || "",
      sortOrder: record.sortOrder ?? 0,
      isActive: record.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: String(values.name || "").trim(),
        description: values.description?.trim() || null,
        sortOrder: values.sortOrder ?? 0,
        isActive: values.isActive !== false,
      };
      if (editingId) {
        await request({
          url: `${apiPath.authAdminIngredientCategoriesList}/${editingId}`,
          method: "PATCH",
          data: payload,
          onSuccess: () => {
            ShowToast("Category updated", Severty.SUCCESS);
            setModalOpen(false);
            fetchRows();
          },
          onError: (err) => {
            ShowToast(apiErrorMessage(err, "Update failed"), Severty.ERROR);
          },
        });
      } else {
        await request({
          url: apiPath.authAdminIngredientCategoriesList,
          method: "POST",
          data: payload,
          onSuccess: () => {
            ShowToast("Category created", Severty.SUCCESS);
            setModalOpen(false);
            fetchRows();
          },
          onError: (err) => {
            ShowToast(apiErrorMessage(err, "Create failed"), Severty.ERROR);
          },
        });
      }
    } catch {
      /* form validation */
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record?.id) return;
    const data = await request({
      url: `${apiPath.authAdminIngredientCategoriesList}/${record.id}`,
      method: "DELETE",
      onSuccess: (d) => {
        ShowToast(d?.message || "Deleted", Severty.SUCCESS);
      },
      onError: (err) => {
        ShowToast(apiErrorMessage(err, "Delete failed"), Severty.ERROR);
      },
    });
    if (data !== null) {
      await fetchRows();
    }
    // On failure, request() returns null and onError already showed toast — resolve normally so
    // Ant Design’s confirm button does not chain Promise.reject(undefined) (uncaught / CRA overlay).
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      className: "user-delete-confirm-modal",
      icon: <ExclamationCircleOutlined style={{ color: "#FAAD14" }} />,
      title: (
        <span style={{ color: "#1f2937", fontWeight: 600 }}>
          {"Delete this category?"}
        </span>
      ),
      content: (
        <div style={{ color: "#4b5563", marginTop: 8 }}>
          {"Only allowed if no ingredients use this Category."}
        </div>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => handleDelete(record),
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 90,
      sorter: (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (v) => (
        <Tag color={v !== false ? "green" : "default"}>
          {v !== false ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              aria-label="Edit category"
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              aria-label="Delete category"
              onClick={() => confirmDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Ingredient Categories"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Category
        </Button>
      }
    >
      <div className="table-responsive">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],

            onChange: (page, pageSize) => {
              setPagination({
                current: page,
                pageSize,
              });
            },

            onShowSizeChange: (current, size) => {
              setPagination({
                current: 1,
                pageSize: size,
              });
            },
          }}
        />
      </div>
      <Modal
        title={editingId ? "Edit Category" : "New Category"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Enter a Category Name" }]}
          >
            <Input placeholder="e.g. Dairy" maxLength={120} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort order">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Optional"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
